import { ContentfulMagnetPage } from "@outschool/gql-backend-generated";
import {
  GetArticlesByIdQuery,
  GetArticlesByIdQueryVariables,
  GetArticlesBySlugQuery,
  GetArticlesBySlugQueryVariables,
} from "@outschool/gql-frontend-generated";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { sampleSize } from "lodash";
import { useEffect, useState } from "react";

import {
  ArticlesByIdQuery,
  ArticlesBySlugQuery,
} from "../Contentful/ContentfulArticles";

/**
 * Defaults to query "homeschool" articles. If a categoryLabel is provided,
 * the article search will use it. In case the result is empty, homeschool
 * articles will be returned.
 *
 * Use "id" to search for a specific set of articles such as "loggedout-homepage".
 *
 *
 * @param {string} [categoryLabel] string to search for articles with that label.
 * @param {string} [id] string to search for specific article collection.
 */
export function useMagnetPageQuery({
  categoryLabel,
  id,
}: {
  categoryLabel?: string;
  id?: string;
}) {
  const hasCategoryLabel = categoryLabel !== undefined;
  const noCategoryLabel = !categoryLabel;
  const hasId = id !== undefined;
  const noId = id === undefined;

  const { loading: homeschoolLoading, data: homeschoolArticles } =
    useQueryWithPreviousData<
      GetArticlesBySlugQuery,
      GetArticlesBySlugQueryVariables
    >(ArticlesBySlugQuery, {
      variables: { categoryLabel: "homeschool" },
      skip: hasId,
    });

  const { loading: defaultLoading, data } = useQueryWithPreviousData<
    GetArticlesBySlugQuery,
    GetArticlesBySlugQueryVariables
  >(ArticlesBySlugQuery, {
    variables: {
      categoryLabel: categoryLabel ?? "homeschool",
    },
    skip: noCategoryLabel || hasId,
  });

  const { loading: idLoading, data: idData } = useQueryWithPreviousData<
    GetArticlesByIdQuery,
    GetArticlesByIdQueryVariables
  >(ArticlesByIdQuery, {
    variables: { id: id ?? "" },
    skip: hasCategoryLabel || noId,
  });

  const [articles, setArticles] = useState<ContentfulMagnetPage[]>([]);
  const loading = defaultLoading || homeschoolLoading || idLoading;
  useEffect(() => {
    if (loading) {
      return;
    }
    if (
      categoryLabel &&
      data?.contentfulMagnetPageCollection?.items?.length &&
      data?.contentfulMagnetPageCollection?.items?.length > 0
    ) {
      setArticles(
        sampleSize(
          data?.contentfulMagnetPageCollection?.items as ContentfulMagnetPage[],
          10
        )
      );
    } else if (
      id &&
      idData?.contentfulFeaturedPagesCollection?.items?.length &&
      idData?.contentfulFeaturedPagesCollection?.items?.length > 0
    ) {
      setArticles(
        idData?.contentfulFeaturedPagesCollection?.items?.[0]?.pagesCollection
          ?.items as ContentfulMagnetPage[]
      );
    } else {
      setArticles(
        sampleSize(
          homeschoolArticles?.contentfulMagnetPageCollection
            ?.items as ContentfulMagnetPage[],
          10
        )
      );
    }
  }, [homeschoolArticles, data, categoryLabel, id, idData, loading]);

  return {
    loading,
    articles,
  };
}
